import { CLI_INPUT_TYPES } from '../../constants';
import { KeyChecks } from '../input/unified/key_checks';
import type { KeyEvent } from '../key_handler';
import type { InputAction } from './actions';
import type { InputKeymapContext } from './context';

interface Binding {
  action: InputAction;
  match: (k: KeyEvent) => boolean;
  when: (c: InputKeymapContext) => boolean;
}

/**
 * Precedence-as-data. First match wins; order encodes precedence explicitly,
 * most specific first. This replaces mount-order arbitration with a single,
 * deterministic table.
 */
/** Inputs the sub-session navigation keys stay live over: the plain prompt and
 * the tool-approval picker, which consumes only ↑/↓/Enter. */
const navigableInput = (c: InputKeymapContext): boolean =>
  c.inputType === CLI_INPUT_TYPES.TEXT || c.inputType === CLI_INPUT_TYPES.CHOICE;

const BINDINGS: Binding[] = [
  {
    action: 'subsession.enterChild',
    match: (k) => /^[1-9]$/.test(k.name) && !k.meta,
    when: (c) => c.subsessionChordArmed,
  },
  {
    action: 'subsession.chordPrev',
    match: (k) => k.name === 'up',
    when: (c) => c.subsessionChordArmed,
  },
  {
    action: 'subsession.chordNext',
    match: (k) => k.name === 'down',
    when: (c) => c.subsessionChordArmed,
  },
  {
    action: 'subsession.chordConfirm',
    match: (k) => KeyChecks.isEnter(k),
    when: (c) => c.subsessionChordArmed,
  },
  {
    // Ctrl+C dismisses like Esc.
    action: 'subsession.chordCancel',
    match: (k) => k.name === 'escape' || KeyChecks.isCtrl('c', k),
    when: (c) => c.subsessionChordArmed,
  },
  {
    action: 'subsession.chordSwallow',
    match: () => true,
    when: (c) => c.subsessionChordArmed,
  },
  // Sub-session bindings hold over navigable inputs only: any other input type
  // means a modal (rejection reason, history search, …) is on top and owns the keys.
  {
    // Esc must not cancel from here, but a silent no-op reads as a freeze — show a hint.
    action: 'subsession.escHint',
    match: (k) => k.name === 'escape',
    when: (c) => c.inSubSession && navigableInput(c),
  },
  {
    action: 'subsession.exit',
    match: (k) => k.name === 'left',
    when: (c) => c.inSubSession && navigableInput(c),
  },
  {
    // Never over a typed draft, which the input swap would destroy; only the
    // TEXT prompt can hold one.
    action: 'subsession.enter',
    match: (k) => KeyChecks.isCtrl('t', k),
    when: (c) =>
      c.viewedNodeHasChildren &&
      !c.dropdownOpen &&
      (c.inputType === CLI_INPUT_TYPES.CHOICE ||
        (c.inputType === CLI_INPUT_TYPES.TEXT && c.messageEmpty)),
  },
  {
    // Ordered before stream.cancel so a queued prompt claims Esc first, but
    // yields to an open dropdown (dropdown.close handles Esc-dismisses-popup)
    // and to non-text inputs like the tool-approval choice picker.
    action: 'queue.cancel',
    match: (k) => k.name === 'escape',
    when: (c) => c.hasQueuedPrompt && !c.dropdownOpen && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    // Esc-dismisses-popup beats stream.cancel: an open dropdown is a modal
    // overlay the user expects to close first. A second Esc (with the dropdown
    // now closed) then enters the cancel-stream two-step as usual.
    action: 'dropdown.close',
    match: (k) => k.name === 'escape' || KeyChecks.isCtrl('c', k),
    when: (c) => c.dropdownOpen,
  },
  {
    action: 'stream.cancel',
    match: (k) => k.name === 'escape',
    when: (c) => c.isLoading,
  },
  {
    action: 'dropdown.next',
    match: (k) => k.name === 'down',
    when: (c) => c.dropdownOpen,
  },
  {
    action: 'dropdown.prev',
    match: (k) => k.name === 'up',
    when: (c) => c.dropdownOpen,
  },
  {
    action: 'dropdown.apply',
    match: (k) => KeyChecks.isTab(k),
    when: (c) => c.dropdownOpen,
  },
  {
    // Fires even while loading: applying a completion just edits the buffer,
    // and a submit that lands here is queued by the controller.
    action: 'dropdown.applyOrSubmit',
    match: (k) => KeyChecks.isEnter(k),
    when: (c) => c.dropdownOpen,
  },
  // Onboarding navigation is only active while the card is on the empty screen,
  // the normal text input is showing, and the user has not started typing.
  // Gated on `inputType === TEXT` so running a step that opens another input
  // (e.g. `/mcp`) hands ↑/↓/Enter to that panel instead of the card. Placed
  // after the dropdown block (so an open dropdown wins) and before
  // `input.submit` (so Enter runs the focused step rather than submitting) —
  // the moment the message is non-empty these fall through to normal input.
  {
    action: 'onboarding.prev',
    match: (k) => k.name === 'up',
    when: (c) => c.onboardingActive && c.messageEmpty && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    action: 'onboarding.next',
    match: (k) => k.name === 'down',
    when: (c) => c.onboardingActive && c.messageEmpty && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    action: 'onboarding.run',
    match: (k) => KeyChecks.isEnter(k),
    when: (c) =>
      c.onboardingActive && c.messageEmpty && !c.isLoading && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  // Welcome-card navigation, gated exactly like onboarding: only on the empty
  // text-input screen before the user starts typing. ←/→ switch the category
  // chip, ↑/↓ move between example rows, Enter runs the focused example. Placed
  // after the dropdown block (an open dropdown wins) and before `input.submit`
  // (so Enter runs the focused example rather than submitting); the moment the
  // message is non-empty these fall through to normal input.
  {
    action: 'welcome.prevCategory',
    match: (k) => k.name === 'left',
    when: (c) =>
      c.welcomeActive && c.messageEmpty && !c.dropdownOpen && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    action: 'welcome.nextCategory',
    match: (k) => k.name === 'right',
    when: (c) =>
      c.welcomeActive && c.messageEmpty && !c.dropdownOpen && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    action: 'welcome.prev',
    match: (k) => k.name === 'up',
    when: (c) => c.welcomeActive && c.messageEmpty && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    action: 'welcome.next',
    match: (k) => k.name === 'down',
    when: (c) => c.welcomeActive && c.messageEmpty && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    action: 'welcome.run',
    match: (k) => KeyChecks.isEnter(k),
    when: (c) =>
      c.welcomeActive && c.messageEmpty && !c.isLoading && c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    // Tab is disabled in sub-session view: there is no prompt to type into,
    // so cycling the agent mode would be a no-op and confusing.
    action: 'agent.cycle',
    match: (k) => KeyChecks.isTab(k),
    when: (c) => c.inputType === CLI_INPUT_TYPES.TEXT && !c.isLoading && !c.inSubSession,
  },
  {
    // Submits even while loading; the controller queues the prompt mid-turn.
    action: 'input.submit',
    match: (k) => KeyChecks.isEnter(k),
    when: (c) => c.inputType === CLI_INPUT_TYPES.TEXT,
  },
  {
    action: 'input.clear',
    match: (k) => KeyChecks.isCtrl('c', k),
    when: (c) => !c.messageEmpty,
  },
  {
    action: 'app.exit',
    match: (k) => KeyChecks.isCtrl('c', k),
    when: () => true,
  },
  {
    action: 'history.open',
    match: (k) => KeyChecks.isCtrl('r', k),
    when: () => true,
  },
  {
    action: 'expand.toggle',
    match: (k) => KeyChecks.isCtrl('o', k),
    when: () => true,
  },
];

/**
 * Resolve a key event to an action given the current context.
 *
 * Pure function: only `press` events resolve to actions; the first binding
 * whose `match` and `when` both hold wins.
 */
export function resolve(k: KeyEvent, c: InputKeymapContext): InputAction | null {
  if (k.eventType !== 'press') {
    return null;
  }

  for (const binding of BINDINGS) {
    if (binding.match(k) && binding.when(c)) {
      return binding.action;
    }
  }

  return null;
}
